import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  forkJoin,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  AnalyticsInfo,
  AnalyticsRangeContext,
  DynamicQueryService,
} from '@services/analytics-services/core';
import { InsightsAnalyticsService } from '@services/analytics-services/insights/insights-analytics.service';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { DateRangePresetType, ProcessingState, Pagination } from '@services/core-services/models';
import { DashboardStore } from '../dashboard/dashboard.store';
import {
  ChartType,
  EventStats,
  QueryType,
  ViewStats,
  VisitorStats,
} from '@services/analytics-services/core/models/matrix-stats';
import { MatrixStatService } from '@services/analytics-services/core/matrix-stat.service';
import { ChartOptions } from '@services/analytics-services/hight-chart-configurations/high-chart-config';
import { DEVICE_COLORS } from '@services/analytics-services/hight-chart-configurations';
import {
  GroupedDataState,
  GroupedDataStoreService,
} from '@services/analytics-services/core/grouped-data-store.service';
import {
  GroupChartTabs,
  GroupedTabData,
} from '@services/analytics-services/core/group-chart.modals';

@Injectable()
export class DashboardInsightsStore implements OnDestroy {
  private activeUsers = new BehaviorSubject<number>(0);
  private activeUsersLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);

  private visitorAnalytics = new BehaviorSubject<AnalyticsInfo<VisitorStats> | undefined>(
    undefined
  );
  private viewAnalytics = new BehaviorSubject<AnalyticsInfo<ViewStats> | undefined>(undefined);
  private eventAnalytics = new BehaviorSubject<AnalyticsInfo<EventStats> | undefined>(undefined);

  private visitorAnalyticsLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private viewAnalyticsLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private eventAnalyticsLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);

  private trafficMatrixData = new BehaviorSubject<any[]>([]);
  private pageStatsChartOptions = new BehaviorSubject<ChartOptions>({});
  private visitorsByDeviceChartOptions = new BehaviorSubject<ChartOptions>({});
  private serverEventsChartOptions = new BehaviorSubject<ChartOptions>({});

  private trafficMatrixLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private pageStatsLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private visitorsByDeviceLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private serverEventsLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);

  private visitorsBySourceState: GroupedDataState;

  private pageStatsError = new BehaviorSubject<string | false>(false);
  private visitorsByDeviceError = new BehaviorSubject<string | false>(false);
  private serverEventsError = new BehaviorSubject<string | false>(false);
  private visitorsBySourceError = new BehaviorSubject<string | false>(false);
  private viewAnalyticsError = new BehaviorSubject<string | false>(false);
  private visitorAnalyticsError = new BehaviorSubject<string | false>(false);
  private eventAnalyticsErrorMessage = new BehaviorSubject<string | false>(false);
  private generalErrorMessage = new BehaviorSubject<string>('');

  private destroy$ = new Subject<void>();

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly insightsAnalyticsService: InsightsAnalyticsService,
    private readonly matrixStatService: MatrixStatService,
    private readonly dynamicQueryService: DynamicQueryService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly groupedDataStoreService: GroupedDataStoreService
  ) {
    this.visitorsBySourceState = this.groupedDataStoreService.createGroupedDataState('source');
    this.dashboardStore
      .getRangeContext()
      .pipe(
        tap(() => this.resetErrors()),
        switchMap(context =>
          forkJoin([
            this.loadActiveUsers(context),
            this.loadVisitorAnalytics(context),
            this.loadViewAnalytics(context),
            this.loadEventAnalytics(context),
            this.loadTrafficMatrixData(context),
            this.loadPageStatsChartData(context),
            this.loadVisitorsByDeviceChartData(context),
            this.loadServerEventsChartData(context),
            this.loadVisitorsBySourceData(context),
          ]).pipe(
            catchError(err => {
              this.handleError('Error loading dashboard insights data', err);
              return of([]);
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Public Getters ---
  getActiveUsers = (): Observable<number> => this.activeUsers.asObservable();
  getActiveUsersLoader = (): Observable<ProcessingState> => this.activeUsersLoader.asObservable();

  getVisitorAnalytics = (): Observable<AnalyticsInfo<VisitorStats> | undefined> =>
    this.visitorAnalytics.asObservable();

  getViewAnalytics = (): Observable<AnalyticsInfo<ViewStats> | undefined> =>
    this.viewAnalytics.asObservable();
  getViewAnalyticsLoader = (): Observable<ProcessingState> =>
    this.viewAnalyticsLoader.asObservable();

  getEventAnalytics = (): Observable<AnalyticsInfo<EventStats> | undefined> =>
    this.eventAnalytics.asObservable();
  getEventAnalyticsLoader = (): Observable<ProcessingState> =>
    this.eventAnalyticsLoader.asObservable();

  getTrafficMatrixData = (): Observable<any[]> => this.trafficMatrixData.asObservable();
  getTrafficMatrixLoader = (): Observable<ProcessingState> =>
    this.trafficMatrixLoader.asObservable();

  getPageStatsChartOptions = (): Observable<ChartOptions> =>
    this.pageStatsChartOptions.asObservable();
  getPageStatsLoader = (): Observable<ProcessingState> => this.pageStatsLoader.asObservable();

  getVisitorsByDeviceChartOptions = (): Observable<ChartOptions> =>
    this.visitorsByDeviceChartOptions.asObservable();
  getVisitorsByDeviceLoader = (): Observable<ProcessingState> =>
    this.visitorsByDeviceLoader.asObservable();

  getServerEventsChartOptions = (): Observable<ChartOptions> =>
    this.serverEventsChartOptions.asObservable();
  getServerEventsLoader = (): Observable<ProcessingState> => this.serverEventsLoader.asObservable();

  // --- Getters for Visitors by Source ---
  getVisitorsBySourceStats = (): Observable<GroupedTabData> =>
    this.visitorsBySourceState.stats.asObservable();

  getVisitorsBySourceTabs = (): Observable<GroupChartTabs[]> =>
    this.visitorsBySourceState.tabs.asObservable();

  getVisitorsBySourceActiveTab = (): Observable<string> =>
    this.visitorsBySourceState.defaultTab.asObservable();

  getVisitorsBySourcePagination = (): Observable<Pagination> =>
    this.visitorsBySourceState.pagination.asObservable() as Observable<Pagination>;

  getVisitorsBySourceLoader = (): Observable<ProcessingState> =>
    this.visitorsBySourceState.loader.asObservable();

  getPageStatsError = (): Observable<string | false> => this.pageStatsError.asObservable();
  getVisitorsByDeviceError = (): Observable<string | false> =>
    this.visitorsByDeviceError.asObservable();
  getServerEventsError = (): Observable<string | false> => this.serverEventsError.asObservable();
  getVisitorAnalyticsErrorMessage = (): Observable<string | false> =>
    this.visitorAnalyticsError.asObservable();
  getViewAnalyticsErrorMessage = (): Observable<string | false> =>
    this.viewAnalyticsError.asObservable();
  getVisitorBySourceError = (): Observable<string | false> =>
    this.visitorsBySourceError.asObservable();
  getEventAnalyticsErrorMessage = (): Observable<string | false> =>
    this.eventAnalyticsErrorMessage.asObservable();
  getGeneralErrorMessage = (): Observable<string> => this.generalErrorMessage.asObservable();

  private resetErrors(): void {
    this.generalErrorMessage.next('');
    this.pageStatsError.next(false);
    this.visitorsBySourceError.next(false);
    this.visitorsByDeviceError.next(false);
    this.serverEventsError.next(false);
    this.eventAnalyticsErrorMessage.next(false);
    this.viewAnalyticsError.next(false);
    this.visitorAnalyticsError.next(false);
  }

  private loadActiveUsers(context: AnalyticsRangeContext): Observable<any> {
    this.activeUsersLoader.next(ProcessingState.Loading);
    return this.insightsAnalyticsService.getActiveUsers(context).pipe(
      tap((response: { active_users?: number }) => {
        this.activeUsers.next(response?.active_users ?? 0);
        this.activeUsersLoader.next(ProcessingState.Success);
      }),
      catchError(err => {
        this.activeUsersLoader.next(ProcessingState.Error);
        this.activeUsers.next(0);
        this.handleError('Error loading active users', err);
        return of(null);
      })
    );
  }

  loadVisitorAnalytics(context: AnalyticsRangeContext): Observable<any> {
    this.visitorAnalyticsLoader.next(ProcessingState.Loading);
    const { range, compare, interval, preset } = context;
    return this.matrixStatService
      .getMatrixAnalytics({
        preset: preset as DateRangePresetType,
        range,
        compare,
        interval,
        query_type: QueryType.METRICS,
        chart_type: ChartType.VISITOR_STATS,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap((analytics: any) => {
          this.visitorAnalytics.next(analytics as AnalyticsInfo<VisitorStats>);
          this.visitorAnalyticsLoader.next(ProcessingState.Success);
        }),
        catchError(err => {
          this.visitorAnalyticsLoader.next(ProcessingState.Error);
          this.visitorAnalytics.next(undefined);
          this.handleError('Error loading visitor analytics', err, this.visitorAnalyticsError);
          return of(null);
        })
      );
  }

  private loadViewAnalytics(context: AnalyticsRangeContext): Observable<any> {
    this.viewAnalyticsLoader.next(ProcessingState.Loading);
    const { range, compare, interval, preset } = context;
    return this.matrixStatService
      .getMatrixAnalytics({
        preset: preset as DateRangePresetType,
        range,
        compare,
        interval,
        query_type: QueryType.METRICS,
        chart_type: ChartType.PAGE_VIEW_STATS,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap((analytics: any) => {
          this.viewAnalytics.next(analytics as AnalyticsInfo<ViewStats>);
          this.viewAnalyticsLoader.next(ProcessingState.Success);
        }),
        catchError(err => {
          this.viewAnalyticsLoader.next(ProcessingState.Error);
          this.viewAnalytics.next(undefined);
          this.handleError('Error loading view analytics', err, this.viewAnalyticsError);
          return of(null);
        })
      );
  }

  private loadEventAnalytics(context: AnalyticsRangeContext): Observable<any> {
    this.eventAnalyticsLoader.next(ProcessingState.Loading);
    const { range, compare, interval, preset } = context;
    return this.matrixStatService
      .getMatrixAnalytics({
        preset: preset as DateRangePresetType,
        range,
        compare,
        interval,
        query_type: QueryType.METRICS,
        chart_type: ChartType.EVENT_STATS,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap((analytics: any) => {
          this.eventAnalytics.next(analytics as AnalyticsInfo<EventStats>);
          this.eventAnalyticsLoader.next(ProcessingState.Success);
        }),
        catchError(err => {
          this.eventAnalyticsLoader.next(ProcessingState.Error);
          this.eventAnalytics.next(undefined);
          this.handleError('Error loading event analytics', err, this.eventAnalyticsErrorMessage);
          return of(null);
        })
      );
  }

  private loadTrafficMatrixData(context: AnalyticsRangeContext): Observable<any> {
    this.trafficMatrixLoader.next(ProcessingState.Loading);
    return this.insightsAnalyticsService.getWebsiteTraffic(context).pipe(
      tap((response: { list?: any[] }) => {
        this.trafficMatrixData.next(response?.list || []);
        this.trafficMatrixLoader.next(ProcessingState.Success);
      }),
      catchError(err => {
        this.trafficMatrixLoader.next(ProcessingState.Error);
        this.trafficMatrixData.next([]);
        this.handleError('Error loading website traffic', err);
        return of(null);
      })
    );
  }

  private loadPageStatsChartData(context: AnalyticsRangeContext): Observable<any> {
    this.pageStatsLoader.next(ProcessingState.Loading);
    this.pageStatsError.next(false);
    return this.insightsAnalyticsService.getPageStat(context).pipe(
      tap((data: { event_name: string; events_count: number }[] = []) => {
        if (!data || data.length === 0) {
          this.pageStatsChartOptions.next({ data: [] });
          this.pageStatsLoader.next(ProcessingState.Empty);
          return;
        }
        const pageStats = data.map(stat => ({
          label: stat.event_name,
          value: stat.events_count,
        }));

        const chartOptions: ChartOptions = {
          categories: pageStats.map(stat => stat.label ?? 'null'),
          data: pageStats.map(stat => ({
            y: stat.value,
            label: stat.label,
          })),
          yAxisTitle: 'Number of Pages',
          tooltipFormatter: (point: Highcharts.Point) => `
            <div class="custom-chart-tooltip" style="padding:12px;">
              <div class="d-flex flex-column spacing-8 align-items-start w-100">
                <h4 class="h4-bold primary-0" style="margin: 0">${point.options.label}</h4>
                <span class="dash-boarder w-100"></span>
                <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                    <div class="d-flex align-items-center spacing-4 w-100 text-center">
                      <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                      <div class="h4-reg primary-50">Page Visits</div>
                    </div>
                    <div class="h4-bold primary-0">${point.options.y}</div>
                </div>
              </div>
            </div>
          `,
        };
        this.pageStatsChartOptions.next(chartOptions);
        this.pageStatsLoader.next(ProcessingState.Success);
      }),
      catchError(err => {
        this.pageStatsLoader.next(ProcessingState.Error);
        this.pageStatsChartOptions.next({ data: [] });
        this.handleError('Error loading page stats', err, this.pageStatsError);
        return of(null);
      })
    );
  }

  private loadVisitorsByDeviceChartData(context: AnalyticsRangeContext): Observable<any> {
    this.visitorsByDeviceLoader.next(ProcessingState.Loading);
    this.visitorsByDeviceError.next(false);
    return this.insightsAnalyticsService.getVisitorsByDevice(context).pipe(
      tap(response => {
        const list = response?.list || [];
        if (list.length === 0) {
          this.visitorsByDeviceChartOptions.next({ data: [] });
          this.visitorsByDeviceLoader.next(ProcessingState.Empty);
          return;
        }
        const deviceData = list.map((item: { device: string; unique_users: number }) => {
          const deviceType = (item.device || 'unknown').toLowerCase();
          return {
            label: item.device || 'Unknown',
            y: item.unique_users,
            color: DEVICE_COLORS[deviceType] || undefined,
          };
        });
        const totalVisitors = deviceData.reduce((sum: any, item: any) => sum + item.y, 0);
        deviceData.sort((a: { y: number }, b: { y: number }) => b.y - a.y);

        const chartOptions: ChartOptions = {
          data: deviceData,
          donut: true,
          title: 'Device Distribution',
          tooltipFormatter: (point: Highcharts.Point) => `
            <div class="custom-chart-tooltip" style="padding:12px;">
              <div class="d-flex flex-column spacing-8 align-items-start w-100">
                <h4 class="h4-bold primary-0" style="margin: 0">${point.name}</h4>
                <span class="dash-boarder w-100"></span>
                <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                  <div class="d-flex align-items-center spacing-4 w-100 text-center">
                    <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                    <div class="h4-reg primary-50">Unique Visitors</div>
                  </div>
                  <div class="h4-bold primary-0">${point.y} (${point.percentage?.toFixed(1)}%)</div>
                </div>
              </div>
            </div>
          `,
          donutOptions: {
            centerText: {
              text: `${totalVisitors.toLocaleString()}`,
              subtext: 'Total Visitors',
            },
          },
        };
        this.visitorsByDeviceChartOptions.next(chartOptions);
        this.visitorsByDeviceLoader.next(ProcessingState.Success);
      }),
      catchError(err => {
        this.visitorsByDeviceLoader.next(ProcessingState.Error);
        this.visitorsByDeviceChartOptions.next({ data: [] });
        this.handleError('Error loading visitors by device', err, this.visitorsByDeviceError);
        return of(null);
      })
    );
  }

  private loadServerEventsChartData(context: AnalyticsRangeContext): Observable<any> {
    this.serverEventsLoader.next(ProcessingState.Loading);
    this.serverEventsError.next(false);
    return this.insightsAnalyticsService.getServerEventsStat(context).pipe(
      tap((list: any = []) => {
        if (!list || list.length === 0) {
          this.serverEventsChartOptions.next({ data: [] });
          this.serverEventsLoader.next(ProcessingState.Empty);
          return;
        }
        const serveEventeData = list.map((item: { event_name: string; events_count: number }) => {
          const deviceType = (item.event_name || 'unknown').toLowerCase();
          return {
            label: item.event_name || 'Unknown',
            y: item.events_count,
            color: DEVICE_COLORS[deviceType] || undefined,
          };
        });
        const totalEvents = serveEventeData.reduce(
          (sum: number, item: { y: number }) => sum + item.y,
          0
        );

        const chartOptions: ChartOptions = {
          data: serveEventeData,
          donut: true,
          title: 'Popular Events',
          tooltipFormatter: (point: Highcharts.Point) => `
            <div class="custom-chart-tooltip" style="padding:12px;">
              <div class="d-flex flex-column spacing-8 align-items-start w-100">
                <h4 class="h4-bold primary-0" style="margin: 0">${point.name}</h4>
                <span class="dash-boarder w-100"></span>
                <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                  <div class="d-flex align-items-center spacing-4 w-100 text-center">
                    <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold"></div>
                    <div class="h4-reg primary-50">Event Counts</div>
                  </div>
                  <div class="h4-bold primary-0">${point.y} (${point.percentage?.toFixed(1)}%)</div>
                </div>
              </div>
            </div>
          `,
          donutOptions: {
            centerText: {
              text: `${totalEvents.toLocaleString()}`,
              subtext: 'Total Events',
            },
          },
        };
        this.serverEventsChartOptions.next(chartOptions);
        this.serverEventsLoader.next(ProcessingState.Success);
      }),
      catchError(err => {
        this.serverEventsLoader.next(ProcessingState.Error);
        this.serverEventsChartOptions.next({ data: [] });
        this.handleError('Error loading server events', err, this.serverEventsError);
        return of(null);
      })
    );
  }

  private loadVisitorsBySourceData(context: AnalyticsRangeContext): Observable<any> {
    this.visitorsBySourceState.loader.next(ProcessingState.Loading);
    this.visitorsBySourceError.next(false);
    return this.insightsAnalyticsService.getVisitors(context).pipe(
      tap(response => {
        const data = this.extractResponseList(response);
        this.groupedDataStoreService.processEntityGroupedData(
          this.visitorsBySourceState,
          data,
          undefined,
          'visitorsBySourcea'
        );
        this.visitorsBySourceState.loader.next(ProcessingState.Success);
      }),
      catchError(err => {
        console.error(`Error loading visitors by source data:`, err);
        this.visitorsBySourceState.loader.next(ProcessingState.Error);
        this.handleError('Error loading visitors by source', err, this.visitorsBySourceError);
        return of({ list: [] });
      })
    );
  }

  private handleError(
    message: string,
    error: any,
    errorSubject?: BehaviorSubject<string | false>
  ): void {
    const specificError = this.errorHandlerService.handleServerError(error);
    const errorMessage = typeof specificError === 'string' ? specificError : message;

    if (errorSubject) {
      errorSubject.next(errorMessage);
    } else {
      this.generalErrorMessage.next(errorMessage);
    }
    console.error(message, error);
  }

  private extractResponseList(response: any): any[] {
    if (response && response.content && Array.isArray(response.content.list)) {
      return response.content.list;
    } else if (response && Array.isArray(response.list)) {
      return response.list;
    } else if (response && Array.isArray(response)) {
      return response;
    }
    console.warn('Could not extract list from response:', response);
    return [];
  }

  updateVisitorsBySourceDefaultTab(tabId: string): void {
    this.groupedDataStoreService.updatePagination(this.visitorsBySourceState, tabId, 1);
    this.visitorsBySourceState.defaultTab.next(tabId);
  }

  updateVisitorsBySourcePagination(tabId: string, page: number, limit: number = 8): void {
    this.groupedDataStoreService.updatePagination(this.visitorsBySourceState, tabId, page, limit);
  }
}
